import React, { useState } from 'react'
import * as yup from 'yup'
import InputMask from 'react-input-mask'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Box,
    CircularProgress,
    DialogContent,
    FormHelperText,
    IconButton,
    SelectChangeEvent,
} from '@mui/material'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import {
    email,
    requiredMobile,
    requiredString,
} from '../../../../utils/validation'
import { H6 } from '../../../../layouts/TextStyles'
import CustomInput from '../../../../components/muiComponents/customInput'
import SingleSelect from '../../../../components/muiComponents/singleSelect'
import { convertToMobile } from '../../../../utils/functions'
import { ManagerState } from '../../../../features/types'
import { managerApi } from '../../../../features/manager/manager'
import { MODERATOR, SELLER } from '../../../../utils/constants'
import { error } from '../styles'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    editManager?: ManagerState
    editMode?: boolean
}

const AddManagerDialog = ({ editManager, editMode }: Props) => {
    const { formatMessage } = useIntl()
    const roles = [
        {
            id: MODERATOR,
            name: formatMessage({
                id: 'moderator',
            }),
        },
        {
            id: SELLER,
            name: formatMessage({ id: 'seller' }),
        },
    ]
    const [open, setOpen] = useState(false)
    const [createManager, { isLoading: isCreating }] =
        managerApi.useCreateManagerMutation()
    const [updateManager, { isLoading: isUpdating }] =
        managerApi.useUpdateManagerMutation()

    const manager: ManagerState = {
        id: editManager?.id || null,
        firstName: editManager?.firstName || '',
        lastName: editManager?.lastName || '',
        email: editManager?.email || '',
        mobile: editManager?.mobile || '',
        role: editManager?.role || '',
    }

    const validationSchema = yup.object().shape(
        {
            firstName: requiredString({ formatMessage }),
            lastName: requiredString({ formatMessage }),
            email: yup
                .string()
                .ensure()
                .when('mobile', {
                    is: '',
                    then: email({ formatMessage }),
                }),
            mobile: yup
                .string()
                .ensure()
                .when('email', {
                    is: '',
                    then: requiredMobile({ formatMessage }),
                }),
            role: requiredString({ formatMessage }),
        },
        [['email', 'mobile']],
    )

    const formik = useFormik({
        initialValues: manager,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editManager) {
                handleUpdateManager()
            } else {
                handleCreateManager()
            }
        },
    })

    const handleCreateManager = async () => {
        try {
            await createManager({
                ...formik.values,
                mobile: convertToMobile(formik.values.mobile),
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) {}
    }

    const handleUpdateManager = async () => {
        try {
            await updateManager({
                ...formik.values,
                mobile: convertToMobile(formik.values.mobile),
            }).unwrap()
            toast.success(formatMessage({ id: 'updatedSuccess' }))
            handleClose()
        } catch (error) {}
    }

    const handleRoleChange = (
        event: SelectChangeEvent<typeof formik.values.role>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('role', value)
    }

    const handleClickOpen = () => {
        setOpen(true)
        formik.setValues(manager)
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <div>
            {editMode ? (
                <IconButton
                    sx={{ background: ui.track }}
                    onClick={handleClickOpen}
                >
                    <img
                        src={EditIcon}
                        alt="edit"
                    />
                </IconButton>
            ) : (
                <Button
                    variant={'contained'}
                    onClick={handleClickOpen}
                >
                    {formatMessage({ id: 'addManager' })}
                </Button>
            )}

            <Dialog
                maxWidth={'md'}
                open={open}
                onClose={handleClose}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title">
                    {editMode
                        ? formatMessage({ id: 'editData' })
                        : formatMessage({ id: 'addManager' })}
                </DialogTitle>

                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'firstName' })}</H6>

                            <CustomInput
                                id="firstName"
                                name="firstName"
                                placeholder={formatMessage({ id: 'firstName' })}
                                fullWidth
                                value={formik.values.firstName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                helperText={formik.errors.firstName}
                                error={Boolean(formik.errors.firstName)}
                                margin="dense"
                            />
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'lastName' })}</H6>

                            <CustomInput
                                id="lastName"
                                name="lastName"
                                placeholder={formatMessage({ id: 'lastName' })}
                                fullWidth
                                value={formik.values.lastName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.lastName)}
                                helperText={formik.errors.lastName}
                                margin="dense"
                            />
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'email' })}</H6>

                            <CustomInput
                                id="email"
                                name="email"
                                type="mail"
                                placeholder={formatMessage({ id: 'email' })}
                                fullWidth
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.email)}
                                helperText={formik.errors.email}
                                margin="dense"
                            />
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'mobile' })}</H6>

                            <InputMask
                                mask="+7 (999) 999-99-99"
                                value={formik.values.mobile}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            >
                                <CustomInput
                                    id="mobile"
                                    name="mobile"
                                    placeholder={formatMessage({
                                        id: 'enterPhone',
                                    })}
                                    fullWidth
                                    helperText={formik.errors.mobile}
                                    error={Boolean(formik.errors.mobile)}
                                    margin="dense"
                                />
                            </InputMask>
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'roles' })}</H6>

                            <SingleSelect
                                id="role"
                                name="role"
                                placeholder={formatMessage({ id: 'roles' })}
                                value={formik.values.role}
                                handleChange={handleRoleChange}
                                handleBlur={formik.handleBlur}
                                menuItems={roles}
                                handleError={Boolean(formik.errors.role)}
                            />

                            <FormHelperText sx={error}>
                                {formik.touched.role && formik.errors.role}
                            </FormHelperText>
                        </Box>
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2, mt: 0 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isCreating || isUpdating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        disabled={isCreating || isUpdating}
                        onClick={() => formik.handleSubmit()}
                    >
                        {isCreating || isUpdating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddManagerDialog
